import { useQuery } from '@tanstack/react-query';
import { useAuth, CreditService } from '@aaas/shared';

interface UseWorkspaceCreditsOptions {
  enableAutoRefresh?: boolean;
}

/**
 * Hook to fetch workspace credits with optional auto-refresh
 * Auto-refresh only happens when enableAutoRefresh is true (e.g., on Billing tab)
 */
export const useWorkspaceCredits = (options?: UseWorkspaceCreditsOptions) => {
  const { user, currentWorkspace } = useAuth();

  return useQuery({
    queryKey: ['workspace-credits', currentWorkspace?.id],
    queryFn: async () => {
      if (!user || !currentWorkspace) {
        throw new Error('User or workspace not available');
      }

      const idToken = await user.getIdToken();
      return CreditService.getWorkspaceBalance(idToken, currentWorkspace.id);
    },
    enabled: !!user && !!currentWorkspace,
    // Auto-refresh every 5 seconds if enabled (e.g., on Billing tab)
    refetchInterval: options?.enableAutoRefresh ? 5000 : false,
    // Only refetch when tab is in focus (don't waste resources in background)
    refetchIntervalInBackground: false,
    staleTime: options?.enableAutoRefresh ? 0 : 1000 * 60 * 5, // Always fresh if auto-refresh, else 5 min
  });
};
